import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  Database,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SchemaEntity {
  type: string // "LodgingBusiness" | "TouristTrip" | "TouristAttraction"
  name: string
  id: string
  status: 'valid' | 'error' | 'warning'
  errors: string[]
  warnings: string[]
  json_ld: Record<string, any>
}

interface ValidationReport {
  total: number
  valid: number
  errors: number
  warnings: number
  entities: SchemaEntity[]
}

interface SchemaValidationProps {
  tenantId: string
}

// ---------------------------------------------------------------------------
// Status badge styles
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<string, string> = {
  valid: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  warning: 'bg-yellow-100 text-yellow-800',
}

const TYPE_STYLES: Record<string, string> = {
  LodgingBusiness: 'bg-blue-100 text-blue-800',
  TouristTrip: 'bg-purple-100 text-purple-800',
  TouristAttraction: 'bg-green-100 text-green-800',
}

// ---------------------------------------------------------------------------
// Actionable guidance helper
// ---------------------------------------------------------------------------

const ENTITY_SECTION_MAP: Record<string, string> = {
  LodgingBusiness: 'Hotels',
  TouristTrip: 'Packages',
  TouristAttraction: 'Activities',
}

function getGuidance(entityType: string, issue: string): string {
  const section = ENTITY_SECTION_MAP[entityType] || 'inventory management'
  // Extract field name from message like "Missing required field 'address' for LodgingBusiness"
  const match = issue.match(/field '(\w+)'/)
  if (match) {
    return `Add '${match[1]}' to your ${entityType} in the ${section} section`
  }
  return `Review your ${entityType} in the ${section} section`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SchemaValidation: React.FC<SchemaValidationProps> = ({ tenantId }) => {
  const [report, setReport] = useState<ValidationReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [previewEntity, setPreviewEntity] = useState<SchemaEntity | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [copied, setCopied] = useState(false)

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchValidation = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/schema/validate?tenant_id=${tenantId}`
      )
      if (!res.ok) {
        throw new Error(`Failed to fetch validation data (${res.status})`)
      }
      const data: ValidationReport = await res.json()
      setReport(data)
    } catch (err: any) {
      console.error('Failed to fetch schema validation:', err)
      setError(err.message || 'Failed to load validation data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchValidation()
  }, [tenantId])

  // ---------------------------------------------------------------------------
  // Row expansion
  // ---------------------------------------------------------------------------

  const toggleRow = (entityId: string) => {
    const next = new Set(expandedRows)
    if (next.has(entityId)) {
      next.delete(entityId)
    } else {
      next.add(entityId)
    }
    setExpandedRows(next)
  }

  // ---------------------------------------------------------------------------
  // Copy JSON-LD
  // ---------------------------------------------------------------------------

  const handleCopy = async (jsonLd: Record<string, any>) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(jsonLd, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for older browsers
      const text = JSON.stringify(jsonLd, null, 2)
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------

  const filteredEntities = report?.entities.filter((entity) => {
    if (typeFilter !== 'all' && entity.type !== typeFilter) return false
    if (statusFilter !== 'all' && entity.status !== statusFilter) return false
    return true
  }) ?? []

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mb-3" />
        <p className="text-sm">Loading Schema.org validation...</p>
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Error state
  // ---------------------------------------------------------------------------

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <AlertCircle className="w-8 h-8 text-red-400 mb-3" />
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchValidation}
          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700"
        >
          Retry
        </button>
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Empty state
  // ---------------------------------------------------------------------------

  if (!report || report.total === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schema.org Validation</h1>
          <p className="text-gray-600">
            Review your structured data markup quality for search engine and AI discoverability.
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-20 text-gray-500 bg-white rounded-lg shadow-sm border border-gray-200">
          <Database className="w-8 h-8 text-gray-300 mb-3" />
          <p className="text-sm">No inventory found.</p>
          <p className="text-xs text-gray-400">
            Add accommodations, packages, or activities to generate Schema.org markup.
          </p>
        </div>
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Schema.org Validation</h1>
        <p className="text-gray-600">
          Review your structured data markup quality for search engine and AI discoverability.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <Database className="w-8 h-8 text-gray-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Entities</p>
              <p className="text-2xl font-semibold text-gray-900">{report.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-green-700">Valid</p>
              <p className="text-2xl font-semibold text-green-700">{report.valid}</p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="flex items-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-red-700">Errors</p>
              <p className="text-2xl font-semibold text-red-700">{report.errors}</p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-8 h-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-yellow-700">Warnings</p>
              <p className="text-2xl font-semibold text-yellow-700">{report.warnings}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Types</option>
          <option value="LodgingBusiness">LodgingBusiness</option>
          <option value="TouristTrip">TouristTrip</option>
          <option value="TouristAttraction">TouristAttraction</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Statuses</option>
          <option value="valid">Valid</option>
          <option value="error">Error</option>
          <option value="warning">Warning</option>
        </select>
        <span className="text-sm text-gray-500 ml-auto">
          {filteredEntities.length} of {report.total} entities shown
        </span>
      </div>

      {/* Entity Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {filteredEntities.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <p className="text-sm">No entities match the current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Issues
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEntities.map((entity) => {
                  const issueCount = entity.errors.length + entity.warnings.length
                  const isExpanded = expandedRows.has(entity.id)
                  return (
                    <React.Fragment key={entity.id}>
                      <tr
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleRow(entity.id)}
                      >
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              TYPE_STYLES[entity.type] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {entity.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {entity.name}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                              STATUS_STYLES[entity.status] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {entity.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {issueCount > 0 ? (
                            <span className="flex items-center gap-1">
                              {issueCount} issue{issueCount !== 1 ? 's' : ''}
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </span>
                          ) : (
                            <span className="text-green-600">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setPreviewEntity(entity)
                            }}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Preview JSON-LD
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (entity.errors.length > 0 || entity.warnings.length > 0) && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 bg-gray-50">
                            <div className="space-y-2">
                              {entity.errors.map((err, idx) => (
                                <div key={`err-${idx}`} className="flex items-start gap-2">
                                  <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-sm text-red-700">{err}</p>
                                    <p className="text-xs text-gray-500">
                                      {getGuidance(entity.type, err)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                              {entity.warnings.map((warn, idx) => (
                                <div key={`warn-${idx}`} className="flex items-start gap-2">
                                  <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-sm text-yellow-700">{warn}</p>
                                    <p className="text-xs text-gray-500">
                                      {getGuidance(entity.type, warn)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview JSON-LD Modal */}
      {previewEntity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  JSON-LD Preview
                </h3>
                <p className="text-sm text-gray-500">
                  {previewEntity.type}: {previewEntity.name}
                </p>
              </div>
              <button
                onClick={() => setPreviewEntity(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <pre className="text-sm text-gray-800 bg-gray-50 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(previewEntity.json_ld, null, 2)}
              </pre>
            </div>
            <div className="flex items-center justify-end px-6 py-4 border-t gap-3">
              <button
                onClick={() => handleCopy(previewEntity.json_ld)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Copy className="w-4 h-4 mr-2" />
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={() => setPreviewEntity(null)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SchemaValidation
